import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import * as moment from 'moment';
import { FileObject } from '../../../core/interfaces/file-object';
import { RiskPreviewHospital, RiskPreviewWard } from '../../../core/utils/risk-location-preview';
import {
  RISK_CATEGORIES,
  RISK_SEVERITIES,
  RiskFacilityChoice,
  RiskFacilityPrediction,
  RiskSuggestion,
  riskCategoryLabel,
  riskResolutionLabel,
  riskSeverityLabel,
} from './risk-suggestion.model';

/** What the caller hands the dialog. */
export interface RiskItemAddDialogData {
  suggestion?: RiskSuggestion | null;
  /**
   * The calendar day AT THE FACILITY (§5c `dateOfIssue`) — the day of the recording, NOT today.
   * A note dictated on Tuesday about Monday's ward round must file under Monday, or J3's timeline
   * grouping and the register's date filter both land on the wrong day.
   */
  dateOfIssue?: any;
  /** The client-side §4 preview (E3). Never sent to the server; see `predictRiskFacility`. */
  facility?: RiskFacilityPrediction | null;
  /** Shown so the doctor knows whose file this lands on — or that it lands on no patient. */
  patientName?: string | null;
  /**
   * `hospitals$` for the practice, WARDS NESTED — the same source `admit-details` reads
   * (`hospital.service.ts` renames `Wards` → `wards` before emitting, which is what
   * `admit-details.component.ts:156-160` consumes as `selectedHospital.wards`).
   *
   * ACTIVE ONLY, by construction: `hospitalController.getHospitalsByGroup` filters
   * `active: true`. So a deactivated hospital cannot be PICKED here — which is correct, because
   * this list is the doctor choosing where to file a NEW issue, not the resolver reconstructing
   * a historical admission. The resolver's wider `where: { groupId }` still applies on the
   * untouched path.
   *
   * `hospitals$` seeds `null` and STAYS null on a fetch failure, so every caller guards and this
   * must tolerate absent/empty. An empty list simply leaves the doctor with In-rooms / OPD and
   * the untouched preview, which is exactly today's behaviour.
   */
  hospitals?: RiskPreviewHospital[] | null;
}

/** What the caller gets back. `null` when the doctor cancelled. */
export interface RiskItemAddDialogResult {
  dateOfIssue: any;
  issue: string;
  category: string;
  severity: string;
  quote: string | null;
  notes: string | null;
  /**
   * The facility the doctor chose, stored as given — or `null` when they left the pre-filled
   * default alone, in which case the server's §4 chain supplies it instead. See
   * `buildFacilityChoice` for why an untouched dialog deliberately sends nothing.
   */
  facilityChoice: RiskFacilityChoice | null;
  /**
   * §28 / D15 — the photographs the doctor attached, as un-uploaded `FileObject`s whose `url` is a
   * `blob:` URL.
   *
   * ⚠ THE CALLER MUST FORWARD THIS ONTO ITS `riskItemData`. This dialog performs no request; it
   * closes with a result and the caller POSTs. A caller that builds `riskItemData` field by field
   * and forgets this key will silently discard every photograph the doctor attached — the save
   * succeeds, the toast is green, and the evidence is gone. `PatientRiskItemService.addRiskItem`
   * turns the array into multipart; an empty one leaves the request as plain JSON.
   *
   * Both current callers forward it: `patient-note-detail.component.ts` and
   * `patient-ai-request-detail.component.ts`.
   */
  media: FileObject[];
}

/**
 * E3 / F3 — CONFIRM BEFORE ADD.
 *
 * Nothing reaches the register without the doctor clicking through this dialog (§1), and R4 names
 * confirm-before-add as one of the three controls on a feature that produces a letterheaded
 * document alleging failures by a named institution. So this dialog does three specific things,
 * and none of them is decoration:
 *
 *  1. It shows the VERBATIM QUOTE, read-only. D8 and §1: "No editing of an item's AI-extracted
 *     quote. The quote is evidence; it is verbatim or it is gone." The server enforces this too
 *     (`quote` is absent from the update whitelist), but the doctor must SEE it here, because
 *     seeing the sentence they actually said is the whole point of persisting it.
 *
 *  2. It shows WHICH FACILITY this will be filed against and WHICH BRANCH of §4 produced it. A
 *     row on this document names a hospital; the doctor gets to see that name before committing.
 *
 *  3. It says out loud that `location: 'rooms'` means IN-ROOMS / OPD — a first-class outcome
 *     under D2, not a failure to resolve. D6 exists precisely so those two are distinguishable,
 *     and a UI that renders branch 4 as an error throws that distinction away.
 *
 * Shared between the AI request detail (E3) and the ward-round note detail (F3) rather than
 * duplicated, for the same reason the suggestion list is shared: a control that exists twice can
 * be relaxed in one copy and not the other.
 */
@Component({
  selector: 'app-risk-item-add-dialog',
  templateUrl: './risk-item-add-dialog.component.html',
  styleUrls: ['./risk-item-add-dialog.component.scss'],
})
export class RiskItemAddDialogComponent {
  readonly categories = RISK_CATEGORIES;
  readonly severities = RISK_SEVERITIES;
  readonly todayDate: moment.Moment = moment();

  /** Editable. `quote` is deliberately NOT in here — see the header. */
  model: {
    dateOfIssue: moment.Moment;
    issue: string;
    category: string;
    severity: string;
    notes: string;
  };

  /** Read-only evidence (D8). */
  readonly quote: string | null;
  /**
   * True when this dialog was opened from the `+` button rather than from an AI suggestion
   * (workstream L / D13). The distinction matters only for the missing-quote message: an AI
   * suggestion that arrived WITHOUT its verbatim quote is a genuine loss of the D8 evidence
   * control and is worth flagging; a hand-typed item never had one and never should, so warning
   * about it trains the user to ignore the warning that does mean something.
   */
  readonly isManualEntry: boolean;
  readonly reason: string | null;
  readonly facility: RiskFacilityPrediction | null;
  readonly patientName: string | null;

  // ══ THE FACILITY PICKER ═══════════════════════════════════════════════════════════════
  //
  // Pre-filled from the §4 preview and EDITABLE — and what it returns is what gets stored, so the
  // preview is a DEFAULT here, not a prediction awaiting confirmation. See `RiskFacilityChoice` in
  // `risk-suggestion.model.ts` for why the client now decides this, and `buildFacilityChoice` for
  // why an untouched picker still sends nothing at all.
  //
  // The hospital → ward dependent select follows `admit-details.component.ts` `selectHospital`:
  // read `selectedHospital.wards` off the already-nested list, sort by name, reset the ward when
  // the hospital changes. Sorted with a plain comparator rather than pulling lodash into a shared
  // dialog for one ordering.

  /** Active hospitals for the practice, wards nested, name-sorted. Possibly empty. */
  readonly hospitals: RiskPreviewHospital[];

  /**
   * THE IN-ROOMS / OPD SENTINEL — the first option of the single facility select.
   *
   * The mode toggle and the hospital select used to be two controls expressing one choice, and two
   * controls can disagree: facility mode with no hospital picked was a reachable, unsaveable state
   * with its own guard, its own banner and its own `canSave` term. One select cannot hold it.
   *
   * A literal rather than an id, so it can never collide with a hospital UUID. Declared here rather
   * than shared with the routed page: `risk-suggestion.model.ts` is the SERVER contract (the four
   * stored columns), and a sentinel that never leaves the browser does not belong in it.
   */
  readonly roomsValue = '__rooms__';

  /**
   * ONE control for the mode AND the hospital: `roomsValue` for an in-rooms / OPD issue, a hospital
   * id otherwise. `null` is reachable but NOT selectable — it is what a preview that resolved a
   * hospital NAME but no ACTIVE hospital (P2) leaves behind, and it renders as a blank select
   * beside the recorded name in the panel above, rather than as a choice the doctor made.
   */
  facilitySelection: string | null = null;
  selectedWardId: string | null = null;

  /** Wards of the currently selected hospital, name-sorted. */
  wards: RiskPreviewWard[] = [];

  /**
   * The pre-filled state, frozen at open. `isFacilityDirty` diffs against THIS, not against the
   * preview object, so that "the doctor changed it" means "the controls moved" and nothing else.
   */
  private readonly initialSelection: string | null;
  private readonly initialWardId: string | null;

  constructor(
    private dialogRef: MatDialogRef<RiskItemAddDialogComponent, RiskItemAddDialogResult | null>,
    @Inject(MAT_DIALOG_DATA) public data: RiskItemAddDialogData
  ) {
    const suggestion = data?.suggestion || null;

    this.isManualEntry = !suggestion;
    this.quote = suggestion?.quote ? String(suggestion.quote) : null;
    this.reason = suggestion?.reason ? String(suggestion.reason) : null;
    this.facility = data?.facility || null;
    this.patientName = data?.patientName || null;

    // ── PRE-FILL THE PICKER FROM THE PREVIEW ────────────────────────────────────────────
    this.hospitals = (Array.isArray(data?.hospitals) ? data!.hospitals! : [])
      .filter(hospital => !!hospital && !!hospital.id)
      .slice()
      .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));

    // Branch 4 ('rooms') pre-fills In-rooms / OPD, which under D2 is a first-class outcome and
    // not a failure — so it is a legitimate PRE-FILLED answer, not an empty form to complete. It
    // is now the FIRST OPTION of the same select the hospitals are in, so that outcome is named as
    // a place sitting alongside the others rather than as the off position of a toggle.
    //
    // Only an id we can actually show as selected. A branch-3 name match that found no ACTIVE
    // hospital (preview P2) leaves `hospitalId` null with a `hospitalName` present: the select
    // then starts EMPTY while the panel above still shows the recorded name. Deliberate — the
    // alternative is inventing a selection the preview did not make.
    const previewIsRooms = !this.facility || this.facility.location === 'rooms';
    const previewHospitalId = this.facility?.hospitalId || null;
    this.facilitySelection = previewIsRooms
      ? this.roomsValue
      : (previewHospitalId && this.hospitals.some(hospital => hospital.id === previewHospitalId)
        ? previewHospitalId
        : null);
    this.syncWards();
    this.selectedWardId = this.matchWardIdByName(this.facility?.wardName);

    this.initialSelection = this.facilitySelection;
    this.initialWardId = this.selectedWardId;

    this.model = {
      dateOfIssue: data?.dateOfIssue ? moment(data.dateOfIssue) : moment(),
      issue: suggestion?.issue ? String(suggestion.issue) : '',
      // The model's category/severity are pre-selected but EDITABLE — the taxonomy is closed
      // (D7) and the doctor, not the extractor, decides which bucket a complaint goes in. An
      // off-taxonomy value from the model falls back rather than being offered as an option the
      // server would then reject with a 400.
      category: this.coerceCategory(suggestion?.category),
      severity: this.coerceSeverity(suggestion?.severity),
      notes: '',
    };
  }

  // ── PICKER BEHAVIOUR ─────────────────────────────────────────────────────────────────

  /**
   * DERIVED, not stored. The concept is still load-bearing — D6's `location` column and
   * `buildFacilityChoice` both speak in these two terms — but it is no longer a control the doctor
   * can move independently of the hospital, which is what made the unsaveable state possible.
   *
   * A `null` selection derives 'facility', which is deliberate and matches the old default: the
   * only way to reach `null` is a previewed hospital that is not among the active ones, and that
   * preview DOES name a facility. It cannot be reached by choosing.
   */
  get facilityMode(): 'facility' | 'rooms' {
    return this.facilitySelection === this.roomsValue ? 'rooms' : 'facility';
  }

  get selectedHospitalId(): string | null {
    const selection = this.facilitySelection;
    return selection && selection !== this.roomsValue ? selection : null;
  }

  /**
   * ONE handler for what used to be `onFacilityModeChange` + `onHospitalChange`, because there is
   * now one control. Both of its jobs survive intact:
   *
   *  - In-rooms / OPD ⇒ `selectedHospitalId` is null ⇒ `syncWards` yields `[]` ⇒ the ward select
   *    disappears and the ward is nulled. 'rooms' means all three snapshot columns NULL (D6).
   *  - A different hospital ⇒ the wards repopulate and the previously chosen ward is CLEARED,
   *    because a ward id from the old hospital would otherwise survive the change and be submitted
   *    against a hospital it does not belong to. The server rejects exactly that, so leaving it
   *    would turn a normal edit into a 400 the doctor cannot explain.
   */
  onFacilityChange(): void {
    this.syncWards();
    this.selectedWardId = null;
  }

  private syncWards(): void {
    const hospital = this.hospitals.find(candidate => candidate.id === this.selectedHospitalId);
    const wards = hospital && Array.isArray(hospital.wards) ? hospital.wards : [];
    this.wards = wards
      .filter(ward => !!ward && !!ward.id)
      .slice()
      .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
  }

  /**
   * The preview yields a ward NAME (branch 1 dereferences `wardId`; branch 3 can only ever have a
   * name — `PatientAdmissionHistory.ward` is a plain string). The select needs an id, so the name
   * is matched back within the chosen hospital using the resolver's own normalisation: trim and
   * lowercase, nothing cleverer. No match leaves the ward unselected rather than guessing.
   */
  private matchWardIdByName(name?: string | null): string | null {
    const target = String(name || '').trim().toLowerCase();
    if (!target) return null;
    const match = this.wards.find(ward => String(ward.name || '').trim().toLowerCase() === target);
    return match?.id || null;
  }

  private hospitalById(id: string | null): RiskPreviewHospital | null {
    return this.hospitals.find(hospital => hospital.id === id) || null;
  }

  /** True once the controls no longer say what they said when the dialog opened. */
  get isFacilityDirty(): boolean {
    return this.facilitySelection !== this.initialSelection
      || this.selectedWardId !== this.initialWardId;
  }

  /**
   * `needsHospitalSelection` USED TO SIT HERE, and it is gone rather than left as a guard that can
   * never fire. It described "the picker was moved into facility mode but no hospital was chosen",
   * which took two controls to express. With one select the only two things a doctor can DO are
   * choose `roomsValue` or choose a hospital id — a mat-select offers no way to select nothing —
   * so `isFacilityDirty === true` now implies a non-null selection, and the state is unreachable
   * by construction rather than by validation.
   *
   * The blank select still happens; it is just never something the doctor did. It is the clean,
   * untouched shape of a preview that resolved a NAME but no ACTIVE hospital (P2), and blocking
   * THAT was always wrong — the untouched path posts no `facilityChoice` at all and the server's
   * §4 chain resolves it. That is why the guard carried an `isFacilityDirty` term in the first
   * place.
   */

  /** What the doctor has currently selected, for the "you changed this" line. */
  get chosenFacilityLine(): string {
    if (this.facilityMode === 'rooms') return 'Your rooms — no hospital or ward';
    const hospital = this.hospitalById(this.selectedHospitalId);
    const ward = this.wards.find(candidate => candidate.id === this.selectedWardId);
    const parts = [hospital?.name, ward?.name].filter(part => !!part);
    return parts.join(' — ') || 'No hospital selected';
  }

  private coerceCategory(value?: string): string {
    const candidate = String(value || '').toLowerCase();
    return (RISK_CATEGORIES as readonly string[]).includes(candidate) ? candidate : 'process';
  }

  private coerceSeverity(value?: string): string {
    const candidate = String(value || '').toLowerCase();
    return (RISK_SEVERITIES as readonly string[]).includes(candidate) ? candidate : 'medium';
  }

  categoryLabel(category: string): string {
    return riskCategoryLabel(category);
  }

  severityLabel(severity: string): string {
    return riskSeverityLabel(severity);
  }

  /** "Inpatient — from the patient's current admission", "In-rooms / OPD — …", etc. */
  get resolutionLabel(): string {
    return riskResolutionLabel(this.facility?.resolvedBy, this.facility?.location);
  }

  /** The facility line: hospital, then ward. Empty for an in-rooms issue, which is correct. */
  get facilityLine(): string {
    const parts = [this.facility?.hospitalName, this.facility?.wardName].filter(part => !!part);
    return parts.join(' — ');
  }

  // `isProvisional` / `provisionalNote` USED TO SIT HERE. They warned that the server might still
  // resolve a facility the browser could not see, which was true while the server was the
  // authority. It no longer is: what this dialog returns is what gets stored. A "this may change
  // once saved" caveat on a field the doctor is about to set by hand is now simply false, so the
  // getters and the two lines that rendered them are gone rather than left to mislead.

  get canSave(): boolean {
    return !!this.model.dateOfIssue && !!String(this.model.issue || '').trim() && !!this.model.category && !!this.model.severity;
  }

  /**
   * The facility to store, as the four columns, or `null` when the picker was never moved.
   *
   * `null` STILL MATTERS, and for a narrower reason than before. An untouched picker posts nothing
   * and the server's §4 chain fills the facility in — which is the better answer in the one case
   * the browser cannot cover: a branch-3 admission naming a hospital that has since been
   * DEACTIVATED resolves to an id on the server and to a bare name here, because
   * `getHospitalsByGroup` only ever returns active rows. Sending the picker's contents back
   * unchanged would drop that id. So an untouched dialog stays out of the way.
   *
   * `location` is decided here rather than server-side: a hospital that the §4 preview had already
   * identified as a booked PROCEDURE stays 'procedure' (the doctor corrected WHERE, not what kind
   * of encounter it was); anything else naming a hospital is 'inpatient'.
   */
  private buildFacilityChoice(): RiskFacilityChoice | null {
    if (!this.isFacilityDirty) return null;

    if (this.facilityMode === 'rooms') {
      return { hospitalId: null, hospitalName: null, wardName: null, location: 'rooms' };
    }

    const hospital = this.hospitalById(this.selectedHospitalId);
    const ward = this.wards.find(candidate => candidate.id === this.selectedWardId) || null;
    return {
      hospitalId: this.selectedHospitalId,
      hospitalName: hospital?.name ? String(hospital.name).trim() : null,
      wardName: ward?.name ? String(ward.name).trim() : null,
      location: this.facility?.location === 'procedure' ? 'procedure' : 'inpatient',
    };
  }

  cancel(): void {
    this.dialogRef.close(null);
  }

  // ═══════════════════════════════════════════════════════════════════════════════════════
  //  §28 / P5 / D15 — PHOTOGRAPHS
  // ═══════════════════════════════════════════════════════════════════════════════════════
  //
  // This dialog injects NO service and performs NO request — it closes with a result and the
  // CALLER does the POST. So the photographs travel out the same way every other field does: as
  // one more key on `RiskItemAddDialogResult`.
  //
  // `[fileObjects]` is bound to a literal `[]` in the template rather than to this array. Nothing
  // is ever pre-loaded here (the item does not exist yet), and binding the working set back into
  // the input would re-append on every emit, because `app-file-upload` re-emits its whole
  // accumulated array in `allowMultiple` mode.

  /** Attachments collected in this dialog. Always new — there is no saved row to round-trip. */
  media: FileObject[] = [];

  /** De-duplicated by `url`; multiple mode re-emits the entire array on each add. */
  onRiskMediaUpload(files: FileObject[]) {
    const incoming = (files || []).filter(
      file => !this.media.some(existing => existing.url === file.url)
    );
    this.media = [...this.media, ...incoming];
  }

  onRiskMediaEdited(files: FileObject[]) {
    this.media = files || [];
  }

  onRiskMediaDelete(fileObject: FileObject) {
    this.media = this.media.filter(file => file.url !== fileObject.url);
  }

  save(): void {
    if (!this.canSave) return;
    this.dialogRef.close({
      dateOfIssue: this.model.dateOfIssue,
      issue: String(this.model.issue).trim(),
      category: this.model.category,
      severity: this.model.severity,
      // Passed straight through, untouched, exactly as the extractor produced it.
      quote: this.quote,
      notes: String(this.model.notes || '').trim() || null,
      facilityChoice: this.buildFacilityChoice(),
      // §28 / D15. The CALLER must forward this onto its `riskItemData` — see the field's doc on
      // `RiskItemAddDialogResult`. Both callers do.
      media: this.media,
    });
  }
}
